import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { services } from '../data/mock';
import * as Icons from 'lucide-react';

const Services = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Nos solutions de nettoyage par drone | PULVISTAR</title>
        <meta name="description" content="Découvrez nos solutions de nettoyage par drone : toitures, panneaux solaires, éoliennes, monuments historiques, bâtiments industriels et copropriétés. Devis gratuit." />
        <link rel="canonical" href="https://pulvistar.fr/services" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-cyan-500/20 text-cyan-200 hover:bg-cyan-500/30 border-0">
              5 domaines d'expertise
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Nos solutions de nettoyage par drone
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
              Des solutions professionnelles personnalisées et adaptées à chaque type d'installation et de support
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4">
          <div className="space-y-16">
            {services.map((service, index) => {
              const IconComponent = Icons[service.icon];
              const isEven = index % 2 === 0;
              
              return (
                <div key={service.id} className={`grid lg:grid-cols-2 gap-12 items-center`}>
                  <div className={`order-1 ${!isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-600 rounded-2xl transform rotate-2 opacity-10"></div>
                      <img
                        src={service.image}
                        alt={service.title}
                        className="relative z-10 rounded-2xl shadow-xl w-full h-80 object-cover"
                      />
                    </div>
                  </div>
                  
                  <div className={`order-2 ${!isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="w-7 h-7 text-blue-900" />
                      </div>
                      <h2 className="text-3xl lg:text-4xl font-bold text-gray-900" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        {service.title}
                      </h2>
                    </div>
                    
                    <p className="text-lg text-gray-600 mb-6" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                      {service.description}
                    </p>
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>
                        Avantages :
                      </h3>
                      <ul className="space-y-2">
                        {service.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-cyan-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700" style={{ fontFamily: '"Inter", sans-serif' }}>
                              {benefit}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <Link to={`/services/${service.slug}`}>
                      <Button className="gap-2 bg-blue-900 hover:bg-blue-800">
                        En savoir plus <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-20 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
              Engagement qualité & sécurité
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-6 text-center border-gray-200">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icons.Shield className="w-8 h-8 text-blue-900" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Assuré & conforme
                </h3>
                <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  Assurance responsabilité civile professionnelle. Conformité aux réglementations en vigueur.
                </p>
              </Card>
              
              <Card className="p-6 text-center border-gray-200">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icons.FileCheck className="w-8 h-8 text-blue-900" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Procédure sécurité
                </h3>
                <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  Protocole strict de sécurisation du chantier. Formation continue de nos pilotes.
                </p>
              </Card>
              
              <Card className="p-6 text-center border-gray-200">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icons.Leaf className="w-8 h-8 text-blue-900" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3" style={{ fontFamily: '"Outfit", sans-serif' }}>
                  Protection surfaces
                </h3>
                <p className="text-gray-600 text-sm" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
                  Produits adaptés au support. Méthode non abrasive respectant l'intégrité des matériaux.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-r from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Outfit", sans-serif', letterSpacing: '-0.5px' }}>
            Besoin d'un devis personnalisé ?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" style={{ fontFamily: '"Inter", sans-serif', lineHeight: '1.7' }}>
            Décrivez votre projet et recevez une proposition détaillée sous 24h
          </p>
          <Button size="lg" className="gap-2 bg-blue-900 hover:bg-blue-800" onClick={() => window.location.href = '/devis'}>
            Demander un devis gratuit <ArrowRight className="w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
